/**
 * Pure cart-view derivations over the pricing engine's CartGroup lines and the
 * store's UiPrint rows. No React, no I/O — unit-tested in cartModel.test.ts.
 *
 * Naming per the FRAMING MODEL: a "loose" group is a bare size+paper line; a
 * "framed" group is keyed by frameId and priced at the coupled framed price.
 */

import {
  framedProductList,
  groupKey,
  parseSizeId,
  sizeLabel,
  type CartGroup,
  type FramedProduct,
  type PricingData,
} from '@rpl/pricing';
import { formatMoney } from '../../lib/format';
import { defaultFrameColour } from '../../lib/sizePaper';
import type { UiPrint } from '../../lib/types';

/** Stable UI key for a group (mirrors the engine's grouping rule). */
export function keyOfGroup(group: CartGroup): string {
  return groupKey(group.sizeId, group.paper, group.frameId);
}

/** Member prints of a cart group, in store order. */
export function groupMembers(prints: readonly UiPrint[], group: CartGroup): UiPrint[] {
  const key = keyOfGroup(group);
  return prints.filter(
    (p) => p.qty > 0 && groupKey(p.sizeId, p.paper, p.frameId) === key,
  );
}

/** Distinct photo ids across a group's members, in first-seen order. */
export function distinctPhotoIds(members: readonly UiPrint[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const m of members) {
    if (!seen.has(m.photoId)) {
      seen.add(m.photoId);
      out.push(m.photoId);
    }
  }
  return out;
}

/**
 * Shorten a paper label for card titles: drop the gsm token, e.g.
 * "Ilford Cotton Rag 310gsm (Smooth)" → "Ilford Cotton Rag (Smooth)".
 */
export function shortPaperLabel(label: string): string {
  return label.replace(/\s*\b\d+gsm\b/g, '').replace(/\s{2,}/g, ' ').trim();
}

/**
 * Compact paper label for summary rows: also drop the brand prefix, e.g.
 * "Ilford Gallerie Pearl 310gsm" → "Gallerie Pearl".
 */
export function compactPaperLabel(label: string): string {
  return shortPaperLabel(label).replace(/^(Ilford|Bauhaus Photo|Bauhaus)\s+/, '');
}

/** Line-card title, e.g. `4 × 6" C-Type · Gloss` / `8 × 10" Framed Print · Ilford Gallerie Pearl`. */
export function groupTitle(group: CartGroup): string {
  const size = group.displayProps['Size'] ?? group.sizeId;
  if (group.frameId) {
    return `${size} Framed Print · ${shortPaperLabel(group.displayProps['Paper'] ?? '')}`;
  }
  if (group.family === 'ctype') {
    return `${size} C-Type · ${group.displayProps['Finish'] ?? ''}`;
  }
  return `${size} Fine Art · ${shortPaperLabel(group.displayProps['Paper'] ?? '')}`;
}

/**
 * Line-card caption: `24 prints across 12 photos · $0.70 each` (the "· bulk"
 * tag is rendered separately). Framed groups get the frame spec instead:
 * `8x10" Black Frame · Print-to-Edge · print included`.
 */
export function groupCaption(group: CartGroup, photoCount: number): string {
  if (group.frameId) {
    const frame = group.displayProps['Frame'] ?? '';
    return `${frame} · print included`;
  }
  const count = `${group.qty} ${group.qty === 1 ? 'print' : 'prints'}`;
  const across = photoCount > 1 ? ` across ${photoCount} photos` : '';
  return `${count}${across} · ${formatMoney(group.effectiveUnitPrice)} each`;
}

/** Compact summary-panel label, e.g. `4×6" Gloss ×24` / `Framed 8×10" Gallerie Pearl ×1`. */
export function summaryLabel(group: CartGroup): string {
  const size = (group.displayProps['Size'] ?? group.sizeId).replace(/\s*×\s*/g, '×');
  if (group.frameId) {
    return `Framed ${size} ${compactPaperLabel(group.displayProps['Paper'] ?? '')} ×${group.qty}`;
  }
  const finish =
    group.family === 'ctype'
      ? group.displayProps['Finish'] ?? ''
      : compactPaperLabel(group.displayProps['Paper'] ?? '');
  return `${size} ${finish} ×${group.qty}`;
}

/**
 * EXACT-match framed product for a LOOSE group (same paper AND print size) in
 * the DEFAULT frame colour, or null. NO LONGER the cart CTA gate — the any-paper upsell (owner 2026-07-26)
 * gates GroupCard on frameOptionsForGroup instead, which offers the full
 * config range. Kept as the "no conversion needed" probe.
 */
export function frameOptionForGroup(
  data: PricingData,
  group: Pick<CartGroup, 'sizeId' | 'paper' | 'frameId'>,
): FramedProduct | null {
  if (group.frameId || !group.paper) return null;
  let size: string;
  try {
    size = parseSizeId(group.sizeId).size;
  } catch {
    return null;
  }
  // Colour matters here too: with two colours stocking the same paper+size this
  // would otherwise return whichever came first in the list.
  const colour = defaultFrameColour(data);
  return (
    framedProductList(data).find(
      (f) => f.paper === group.paper && f.printSize === size && f.colour === colour,
    ) ?? null
  );
}

/** Display label for a framed option's print size, e.g. `8 × 10"`. */
export function frameOptionSizeLabel(option: FramedProduct): string {
  return sizeLabel(option.printSize, option.printInches);
}

/**
 * Group-stepper semantics for SINGLE-PHOTO lines (documented behavior): the
 * stepper adds/removes copies of the LAST print row in the group. `+` bumps
 * its qty; `−` decrements it, removing the row when its qty hits 0 — but never
 * removes the group's final print (the line minimum is 1; removing a line
 * happens in Size & Paper, or via "Remove frame" for framed lines).
 *
 * ROUND 7: lines spanning MULTIPLE photos no longer use this blindly (it
 * silently bumped whichever photo happened to be last) — their +/− and number
 * open the per-photo quantity editor instead (photoQtyRows / perPhotoQtyPlan),
 * so the customer says exactly which photo gets more prints.
 */
export type StepperAction =
  | { kind: 'inc'; printUiId: string; qty: number }
  | { kind: 'dec'; printUiId: string; qty: number }
  | { kind: 'remove'; printUiId: string }
  | { kind: 'none' };

export function stepGroupQty(members: readonly UiPrint[], delta: 1 | -1): StepperAction {
  const last = members[members.length - 1];
  if (!last) return { kind: 'none' };
  if (delta === 1) return { kind: 'inc', printUiId: last.id, qty: last.qty + 1 };
  if (last.qty > 1) return { kind: 'dec', printUiId: last.id, qty: last.qty - 1 };
  if (members.length > 1) return { kind: 'remove', printUiId: last.id };
  return { kind: 'none' }; // last remaining print at qty 1 — line minimum
}

/** One row change produced by setGroupQtyPlan; qty 0 means remove the row. */
export interface GroupQtyPatch {
  printUiId: string;
  qty: number;
}

/**
 * Direct-entry group quantity for SINGLE-PHOTO lines (the click-to-type
 * stepper): the row patches that take a group from its current total to
 * `target`, using the SAME semantics as repeatedly pressing +/− (grow the last
 * row; shrink from the last row back, removing emptied rows but never the
 * group's final print). `target` clamps to [1, 999]; a no-op returns [].
 * Multi-photo lines use perPhotoQtyPlan via the per-photo editor instead.
 */
export function setGroupQtyPlan(members: readonly UiPrint[], target: number): GroupQtyPatch[] {
  if (members.length === 0) return [];
  const goal = Math.min(999, Math.max(1, Math.floor(target)));
  const qtys = members.map((m) => m.qty);
  const sum = qtys.reduce((a, b) => a + b, 0);
  if (goal === sum) return [];
  if (goal > sum) {
    const last = members[members.length - 1]!;
    return [{ printUiId: last.id, qty: last.qty + (goal - sum) }];
  }
  let deficit = sum - goal;
  for (let i = qtys.length - 1; i >= 0 && deficit > 0; i--) {
    const isFinalRow = qtys.slice(0, i).every((q) => q === 0);
    const floor = isFinalRow ? 1 : 0;
    const take = Math.min(deficit, qtys[i]! - floor);
    qtys[i]! -= take;
    deficit -= take;
  }
  const patches: GroupQtyPatch[] = [];
  members.forEach((m, i) => {
    if (qtys[i] !== m.qty) patches.push({ printUiId: m.id, qty: qtys[i]! });
  });
  return patches;
}

/* ============================================================
   Per-photo quantity editor model (round 7)
   ============================================================ */

/** One photo's line in the per-photo quantity editor. */
export interface PhotoQtyRow {
  photoId: string;
  /** The photo's total prints within this group (its member qtys summed). */
  qty: number;
}

/**
 * Bucket a group's member prints per photo (first-seen order) with summed
 * quantities — the rows of the per-photo quantity editor.
 */
export function photoQtyRows(members: readonly UiPrint[]): PhotoQtyRow[] {
  const out: PhotoQtyRow[] = [];
  const idx = new Map<string, number>();
  for (const m of members) {
    const i = idx.get(m.photoId);
    if (i == null) {
      idx.set(m.photoId, out.length);
      out.push({ photoId: m.photoId, qty: m.qty });
    } else {
      out[i]!.qty += m.qty;
    }
  }
  return out;
}

/**
 * The print patches that set each photo's quantity within a group to the
 * drafted target (photoId → qty). Per photo: growth lands on its LAST member
 * print; shrink walks its member prints from the last back (a print emptied to
 * 0 means "remove it" — callers map qty ≤ 0 patches to removePrint). A target
 * of 0 removes that photo's prints from the line entirely, so callers must
 * keep the GROUP total ≥ 1 themselves (the editor disables committing an
 * all-zero draft). Photos not in `targets` are untouched; targets clamp to
 * [0, 999].
 */
export function perPhotoQtyPlan(
  members: readonly UiPrint[],
  targets: ReadonlyMap<string, number>,
): GroupQtyPatch[] {
  const byPhoto = new Map<string, UiPrint[]>();
  for (const m of members) {
    const list = byPhoto.get(m.photoId);
    if (list) list.push(m);
    else byPhoto.set(m.photoId, [m]);
  }
  const patches: GroupQtyPatch[] = [];
  for (const [photoId, prints] of byPhoto) {
    const want = targets.get(photoId);
    if (want == null) continue;
    const goal = Math.min(999, Math.max(0, Math.floor(want)));
    const sum = prints.reduce((a, p) => a + p.qty, 0);
    if (goal === sum) continue;
    if (goal > sum) {
      const last = prints[prints.length - 1]!;
      patches.push({ printUiId: last.id, qty: last.qty + (goal - sum) });
      continue;
    }
    let deficit = sum - goal;
    const qtys = prints.map((p) => p.qty);
    for (let i = qtys.length - 1; i >= 0 && deficit > 0; i--) {
      const take = Math.min(deficit, qtys[i]!);
      qtys[i]! -= take;
      deficit -= take;
    }
    prints.forEach((p, i) => {
      if (qtys[i] !== p.qty) patches.push({ printUiId: p.id, qty: qtys[i]! });
    });
  }
  return patches;
}

/**
 * Per-PRINT quantity editor plan (round 9). When a qty control acts at PHOTO
 * level but the photo has MORE THAN ONE distinct print (different size /
 * paper / border / frame), the customer picks which print to add or remove in
 * the per-print editor — this turns its drafts (print ui id → qty) into row
 * patches. A print drafted to 0 means "remove it" (callers map qty ≤ 0
 * patches to removePrint) and callers keep the photo's total ≥ 1 themselves
 * (the editor disables an all-zero commit). Prints not in `targets` are
 * untouched; targets clamp to [0, 999].
 */
export function perPrintQtyPlan(
  members: readonly UiPrint[],
  targets: ReadonlyMap<string, number>,
): GroupQtyPatch[] {
  const patches: GroupQtyPatch[] = [];
  for (const m of members) {
    const want = targets.get(m.id);
    if (want == null) continue;
    const goal = Math.min(999, Math.max(0, Math.floor(want)));
    if (goal !== m.qty) patches.push({ printUiId: m.id, qty: goal });
  }
  return patches;
}

/**
 * The quantity the "Even out" button sets every photo to: the MOST COMMON
 * per-photo quantity (ties → the smallest), so one accidental bump evens back
 * to what the rest of the group already has. 0-qty drafts don't vote unless
 * everything is 0.
 */
export function evenQty(rows: ReadonlyArray<{ qty: number }>): number {
  const counts = new Map<number, number>();
  for (const r of rows) {
    if (r.qty > 0) counts.set(r.qty, (counts.get(r.qty) ?? 0) + 1);
  }
  if (counts.size === 0) return 1;
  let best = 0;
  let bestCount = -1;
  for (const [qty, count] of counts) {
    if (count > bestCount || (count === bestCount && qty < best)) {
      best = qty;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Border-split caption fragment for a size+paper cart line whose member prints
 * mix border modes (borders are price-neutral, so the LINE stays merged for
 * Shopify but the caption must reveal the split). Counts are print quantities,
 * e.g. `1 with white border` / `2 with white border · 1 with handling border`.
 * Returns null when no member has a border.
 */
export function borderSplitCaption(members: readonly UiPrint[]): string | null {
  let white = 0;
  let handling = 0;
  for (const m of members) {
    if (m.frameId) continue;
    if (m.borderMode === 'white') white += m.qty;
    else if (m.borderMode === 'handling') handling += m.qty;
  }
  const parts: string[] = [];
  if (white > 0) parts.push(`${white} with white border`);
  if (handling > 0) parts.push(`${handling} with handling border`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Free-shipping nudge state (small-format offer): eligible only when EVERY
 * live print is a loose C-Type — any frame or fine-art/custom print
 * disqualifies the whole order (those ship differently). Threshold comes from
 * pricing.freeShipping (D1-editable, seed fallback handled by the caller).
 */
export interface FreeShipState {
  eligible: boolean;
  threshold: number;
  /** Dollars still to add (0 when unlocked). */
  remaining: number;
  unlocked: boolean;
  /** 0..1 toward the threshold, for the progress track. */
  progress: number;
}

export function freeShipState(
  threshold: number,
  prints: readonly UiPrint[],
  subtotal: number,
): FreeShipState {
  const live = prints.filter((p) => p.qty > 0);
  const eligible =
    live.length > 0 &&
    threshold > 0 &&
    live.every((p) => !p.frameId && parseSizeId(p.sizeId).family === 'ctype');
  const unlocked = eligible && subtotal >= threshold;
  return {
    eligible,
    threshold,
    remaining: unlocked ? 0 : Math.max(0, threshold - subtotal),
    unlocked,
    progress: Math.max(0, Math.min(1, subtotal / threshold)),
  };
}
